import React, { forwardRef, useImperativeHandle, useState, useCallback, useEffect } from 'react';
import { Form, Input, Select, Divider, Button } from 'antd';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { countryMap, stateMap } from '../utils/constants';
import {
  PaymentMethodFormProps,
  PaymentMethodFormRef,
  PaymentFormData,
  PaymentMethodError,
  PaymentMethodFormBaseProps
} from '../types/payment';
import {
  CREDIT_CARD_LOGOS,
  CARD_ELEMENT_OPTIONS
} from '../constants/payment';
import { StyledCardContainer } from '../styles/payment';
import { usePaymentMethod } from '../hooks/usePaymentMethod';
import { withAdapter } from '../utils/micro-fe';
import { withTheme } from './WithTheme';
import { DownOutlined, EyeInvisibleOutlined, CreditCardOutlined, EyeOutlined } from '@ant-design/icons';
import { withStripeElements } from './withStripeElements';

export const PaymentMethodFormBase = React.memo(forwardRef<PaymentMethodFormRef, PaymentMethodFormBaseProps>((props, ref) => {
  // All logic uses props instead of hook variables
  // Component renders based on props.formData, props.savedCard, etc.
  const [form] = Form.useForm<PaymentFormData>();

  const {
    inputClassName,
    inputSuffixIconColor,
    selectDropdownStyle,
    selectIconStyle,
    selectStyle,
    cardElementOptions = {},
    cardElementStyles = {},
    cardErrors,
    cardBrand,
    cardComplete,
    cardNumberComplete,
    cardExpiryComplete,
    cardCvcComplete,
    savedCard,
    isLoading,
    isProcessing,
    formData,
    handleCardChange,
    handleSubmit,
    clearErrors,
    clearCard,
    resetCardCompletionStates,
    setFormField,
    fetchExistingPaymentMethod,
    elements,
    saveAsInvoiceAddress,
    onFormValidation
  } = props;

  // Expose submit function and refetch function through ref
  useImperativeHandle(ref, () => ({
    submit: async () => {
      try {
        // Additional validation for card completion
        if (savedCard && !cardFieldsEdited) {
          // Using saved card without editing card fields - no need to validate card elements
        } else if (!savedCard && !cardComplete) {
          // New card - validate all elements are complete
          return { error: true, message: 'Please complete all card details' } as PaymentMethodError;
        } else if (cardFieldsEdited && !cardComplete) {
          // Card fields were edited - validate all elements are complete
          return { error: true, message: 'Please complete all card details' } as PaymentMethodError;
        }

        // If validation passes, try to submit the payment method
        try {
          const data = await handleSubmit(formData, cardFieldsEdited, saveAsInvoiceAddress);

          // If card fields were edited successfully, reset the edited state
          if (cardFieldsEdited && data && !('error' in data)) {
            setCardFieldsEdited(false);
            setRevealed(false);
          }

          return data;
        } catch (paymentError: any) {
          // Payment processing error - return error object to keep modal open
          console.error('💥 Payment processing error:', paymentError);
          return { error: true, message: paymentError.message } as PaymentMethodError;
        }
      } catch (validationError: any) {
        // This catch block handles any actual errors thrown during validation
        console.error('💥 Form validation error:', validationError);
        return { error: true, message: validationError.message } as PaymentMethodError;
      }
    },
    refetchPaymentMethod: async () => {
      await fetchExistingPaymentMethod();
    },
    resetForm: () => {
      // Reset form state when modal is closed/cancelled
      setCardFieldsEdited(false);
      setRevealed(false);
      setTouchedFields({
        cardNumber: false,
        expiration: false,
        cvc: false
      });
      setBlurredFields({
        cardNumber: false,
        expiration: false,
        cvc: false
      });
    },
    getFormData: () => {
      return formData;
    },
    getSavedCard: () => {
      return savedCard;
    },
  }));

  const [revealed, setRevealed] = useState(false);
  const [cardFieldsEdited, setCardFieldsEdited] = useState(false);
  const [touchedFields, setTouchedFields] = useState({
    cardNumber: false,
    expiration: false,
    cvc: false
  });

  const [blurredFields, setBlurredFields] = useState({
    cardNumber: false,
    expiration: false,
    cvc: false
  });

  // Track when card fields are touched
  const markFieldAsTouched = useCallback((fieldName: 'cardNumber' | 'expiration' | 'cvc') => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Track when card fields are blurred (user leaves the field)
  const markFieldAsBlurred = useCallback((fieldName: 'cardNumber' | 'expiration' | 'cvc') => {
    setBlurredFields(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Track when card fields are edited
  const handleCardFieldChange = useCallback((event: any) => {
    if (savedCard && !cardFieldsEdited) {
      // User is starting to edit card fields - reset cardComplete to false
      // so we validate the new fields instead of the saved card
      setCardFieldsEdited(true);
    }

    // Mark field as touched when user interacts with it
    if (event.elementType === 'cardNumber') {
      markFieldAsTouched('cardNumber');
    } else if (event.elementType === 'cardExpiry') {
      markFieldAsTouched('expiration');
    } else if (event.elementType === 'cardCvc') {
      markFieldAsTouched('cvc');
    }

    // Note: Blur events will be handled by onBlur handlers on the container elements

    handleCardChange(event);
  }, [savedCard, cardFieldsEdited, handleCardChange, markFieldAsTouched, markFieldAsBlurred]);


  // Reset card completion states when editing starts
  useEffect(() => {
    if (cardFieldsEdited && savedCard) {
      // When user starts editing, reset the card completion states
      // so they can be properly validated as the user types
      resetCardCompletionStates();
    }
  }, [cardFieldsEdited, savedCard, resetCardCompletionStates]);

  // Generate validation errors for incomplete blurred fields
  const getFieldValidationError = useCallback((fieldName: 'cardNumber' | 'expiration' | 'cvc') => {
    const isBlurred = blurredFields[fieldName];
    const hasStripeError = cardErrors[fieldName];

    // Don't show validation errors if we're processing (prevents error flash during success)
    if (isProcessing) {
      return undefined;
    }

    // If field is blurred and incomplete, show validation error
    if (isBlurred && !hasStripeError) {
      if (fieldName === 'cardNumber' && !cardNumberComplete) {
        return 'Please enter a valid card number';
      }
      if (fieldName === 'expiration' && !cardExpiryComplete) {
        return 'Please enter a valid expiration date';
      }
      if (fieldName === 'cvc' && !cardCvcComplete) {
        return 'Please enter a valid security code';
      }
    }

    return hasStripeError || undefined;
  }, [blurredFields, cardErrors, cardNumberComplete, cardExpiryComplete, cardCvcComplete, isProcessing]);

  const beginEditingCard = useCallback((elementType: 'cardNumber' | 'cardExpiry' | 'cardCvc') => {
    setCardFieldsEdited(true);

    // Mark the field as touched when user starts editing
    if (elementType === 'cardNumber') {
      markFieldAsTouched('cardNumber');
    } else if (elementType === 'cardExpiry') {
      markFieldAsTouched('expiration');
    } else if (elementType === 'cardCvc') {
      markFieldAsTouched('cvc');
    }

    // Use setTimeout to ensure the element is visible before focusing
    setTimeout(() => {
      const element = elements.getElement(elementType as any);
      element?.focus();
    }, 100);
  }, [elements, markFieldAsTouched]);

  // Form validation logic
  const validateForm = useCallback(() => {
    if (!onFormValidation) return;

    // Check if all required fields are filled
    const requiredFields = ['cardholderName', 'addressLine1', 'city', 'state', 'country', 'zipCode'];
    const hasRequiredFields = requiredFields.every(field => formData[field as keyof PaymentFormData]);

    // Check if card is complete
    // If user has saved card and hasn't edited card fields, card is complete
    // If user has saved card and edited card fields, check if new card is complete
    // If user has no saved card, check if new card is complete
    const isCardComplete = savedCard && !cardFieldsEdited ? true : cardComplete;

    // Check if there are any card errors
    const hasCardErrors = Object.values(cardErrors).some(error => error);

    const isValid = hasRequiredFields && isCardComplete && !hasCardErrors;
    onFormValidation(isValid);
  }, [formData, savedCard, cardComplete, cardErrors, cardFieldsEdited, onFormValidation]);

  // Validate form whenever relevant data changes
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const component = (
    <div>
      <style>
        {`
          .anticon-eye-invisible.ant-input-password-icon svg path,
          .anticon.anticon-eye.ant-input-password-icon svg path,
          .anticon.anticon-credit-card svg path {
            fill: ${inputSuffixIconColor || 'currentColor'};
          }

          /* Card element error states - now handled in index.css */
        `}
      </style>
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">Loading payment method...</div>
        </div>
      ) : (
        <Form
          layout="vertical"
          requiredMark={false}
          className={props.formClassName}
          initialValues={formData}
          form={form}
          disabled={isProcessing}
          onValuesChange={validateForm}
        >
          {/* Card logos strip */}
          <div className="flex flex-col mt-[8px] mb-[8px]">
            <span className="text-sm font-medium mb-2">Card</span>
            <div className="flex gap-[18px] -ml-2">
              {CREDIT_CARD_LOGOS.map((logo) => (
                <div
                  key={logo.name}
                  className={`flex items-center justify-center rounded-md overflow-hidden w-14 h-12 ${cardBrand === logo.name.toLowerCase() ? 'border-2 border-blue-500' : ''}`}
                >
                  <img src={logo.image} alt={logo.name} className="w-full h-full" />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#FFFFFF1A] h-[1px] w-full mt-[16px] mb-[24px]" />
          {/* Card number */}
          <Form.Item
            label="Card Number"
            name="cardNumber"
            validateStatus={getFieldValidationError('cardNumber') ? 'error' : undefined}
            help={getFieldValidationError('cardNumber')}
            required
            style={{ fontFamily: 'Inter' }}
          >
            <StyledCardContainer
              {...cardElementStyles}
              className={`relative ${cardElementStyles.containerClassName || ''}`}
              disabled={isProcessing}
            >
              {savedCard && !cardFieldsEdited && (
                <div className="saved-card-info flex items-center justify-between">
                  <div
                    className="flex-1 hover:opacity-80 transition-opacity cursor-text"
                    onClick={() => beginEditingCard('cardNumber')}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        beginEditingCard('cardNumber');
                      }
                    }}
                  >
                    <span className="tracking-wider text-[16px] font-medium">
                      {revealed ? `•••• •••• •••• ${savedCard.last4}` : '•••• •••• •••• ••••'}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="Show card number"
                    className="p-1 pt-[10px] rounded-md hover:opacity-80 transition-opacity ml-2"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the edit mode
                      setRevealed(!revealed);
                    }}
                    title="Show card number"
                  >
                    {revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                </div>
              )}

              {/* Always render card elements, use CSS to show/hide */}
              <div
                className={`card-elements-container ${savedCard && !cardFieldsEdited ? 'opacity-0 pointer-events-none absolute inset-0' : 'opacity-100'}`}
              >
                <div className="flex items-center">
                  <div className="flex-1">
                    <CardNumberElement
                      options={{
                        ...CARD_ELEMENT_OPTIONS,
                        ...cardElementOptions,
                      }}
                      onChange={handleCardFieldChange}
                      onBlur={() => markFieldAsBlurred('cardNumber')}
                      className={cardElementStyles.className}
                    />
                  </div>
                  <div className="ml-3 opacity-70">
                    <CreditCardOutlined />
                  </div>
                </div>
              </div>
            </StyledCardContainer>
          </Form.Item>
          <div className="flex gap-[16px]">
            <div className="flex-1">
              <StyledCardContainer
                {...cardElementStyles}
                className={`${cardElementStyles.containerClassName || ''}`}
                disabled={isProcessing}
              >
                <Form.Item
                  label="Expiration date"
                  name="expirationDate"
                  validateStatus={getFieldValidationError('expiration') ? 'error' : undefined}
                  help={getFieldValidationError('expiration')}
                  rules={[{ required: true, message: 'Please enter your expiration date' }]}
                >
                  {savedCard && !cardFieldsEdited ? (
                    <div
                      className="saved-card-info flex items-center justify-between py-3 px-4 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors cursor-text"
                      onClick={() => beginEditingCard('cardExpiry')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          beginEditingCard('cardExpiry');
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[16px] font-medium">
                          {savedCard.expMonth?.toString().padStart(2, '0')} / {savedCard.expYear}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <CardExpiryElement
                      options={{
                        ...CARD_ELEMENT_OPTIONS,
                        ...cardElementOptions,
                        placeholder: 'MM / YY',
                      }}
                      onChange={handleCardFieldChange}
                      onBlur={() => markFieldAsBlurred('expiration')}
                      className={cardElementStyles.className}
                    />
                  )}
                </Form.Item>
              </StyledCardContainer>
            </div>
            <div className="flex-1">
              <StyledCardContainer
                {...cardElementStyles}
                className={`${cardElementStyles.containerClassName || ''}`}
                disabled={isProcessing}
              >
                <Form.Item
                  label="Security code"
                  name="securityCode"
                  validateStatus={getFieldValidationError('cvc') ? 'error' : undefined}
                  help={getFieldValidationError('cvc')}
                  rules={[{ required: true, message: 'Please enter your security code' }]}
                >
                  {savedCard && !cardFieldsEdited ? (
                    <div
                      className="saved-card-info flex items-center justify-between py-3 px-4 bg-gray-50 rounded-md border border-gray-200 hover:bg-gray-100 transition-colors cursor-text"
                      onClick={() => beginEditingCard('cardCvc')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          beginEditingCard('cardCvc');
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[16px] font-medium">
                          ••••
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <div className="flex-1">
                        <CardCvcElement
                          options={{
                            ...CARD_ELEMENT_OPTIONS,
                            ...cardElementOptions,
                            placeholder: '000',
                          }}
                          onChange={handleCardFieldChange}
                          onBlur={() => markFieldAsBlurred('cvc')}
                          className={cardElementStyles.className}
                        />
                      </div>
                      <div className="ml-3 opacity-70">
                        <CreditCardOutlined />
                      </div>
                    </div>
                  )}
                </Form.Item>
              </StyledCardContainer>
            </div>
          </div>
          {/* Cardholder + Address */}
          <Form.Item
            label="Cardholder name"
            name="cardholderName"
            rules={[{ required: true, message: 'Please enter the cardholder name' }]}
          >
            <Input
              className={inputClassName}
              placeholder="Full name"
              value={formData.cardholderName}
              onChange={(e) => setFormField('cardholderName', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Address Line 1"
            name="addressLine1"
            rules={[{ required: true, message: 'Please enter your address' }]}
          >
            <Input
              className={inputClassName}
              placeholder="123 Street Ave."
              value={formData.addressLine1}
              onChange={(e) => setFormField('addressLine1', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Address Line 2" name="addressLine2">
            <Input
              className={inputClassName}
              placeholder="Apt, suite, etc."
              value={formData.addressLine2}
              onChange={(e) => setFormField('addressLine2', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="City"
            name="city"
            rules={[{ required: true, message: 'Please enter your city' }]}
          >
            <Input
              className={inputClassName}
              value={formData.city}
              onChange={(e) => setFormField('city', e.target.value)}
            />
          </Form.Item>

          <div className="flex gap-[16px]">
            <Form.Item
              label="Country"
              name="country"
              rules={[{ required: true, message: 'Please select your country' }]}
              className="flex-1"
            >
              <Select
                style={selectStyle}
                className={inputClassName}
                dropdownStyle={selectDropdownStyle}
                suffixIcon={<DownOutlined style={selectIconStyle} />}
                value={formData.country}
                onChange={(value) => setFormField('country', value)}
                placeholder="Select country"
              >
                {Object.entries(countryMap).map(([code, name]) => (
                  <Select.Option key={code} value={code} className="billing-ui">{name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: 'Please select your state' }]}
              className="flex-1"
            >
              <Select
                style={selectStyle}
                className={inputClassName}
                dropdownStyle={selectDropdownStyle}
                suffixIcon={<DownOutlined style={selectIconStyle} />}
                value={formData.state}
                onChange={(value) => setFormField('state', value)}
                placeholder="Select state"
              >
                {Object.entries(stateMap).map(([code, name]) => (
                  <Select.Option key={code} value={code} className="billing-ui">{name}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="ZIP / Postal code"
              name="zipCode"
              rules={[{ required: true, message: 'Please enter your ZIP code' }]}
              className="flex-1"
            >
              <Input
                className={inputClassName}
                value={formData.zipCode}
                onChange={(e) => setFormField('zipCode', e.target.value)}
              />
            </Form.Item>
          </div>
        </Form>
      )}
    </div>
  );

  return props.theme ? withTheme({ children: component, theme: props.theme }) : component;
}));

export const PaymentMethodForm = forwardRef<PaymentMethodFormRef, PaymentMethodFormProps>((props, ref) => {
  const hookData = usePaymentMethod({ entityId: props.entityId });

  const component = (
    <PaymentMethodFormBase
      {...props}
      {...hookData}
      ref={ref}
    />
  );

  return props.theme ? withTheme({ children: component, theme: props.theme }) : component;
});

export default withAdapter<PaymentMethodFormProps>(withStripeElements(PaymentMethodForm));
